import { consentLogger } from './consentLogger';
import { withWindow } from './withWindow';

/**
 * Polaris consent categories used in data-polaris-consent attributes
 */
export type ConsentCategory = 'essential' | 'statistics' | 'marketing';

/**
 * Consent state for all categories
 */
export type ConsentState = Readonly<{
  essential: boolean;
  statistics: boolean;
  marketing: boolean;
}>;

/**
 * Consent state when the visitor's choice cannot be read or trusted: only essential
 * scripts run. Every rule in this module fails closed to this state.
 */
const CONSENT_DENIED_STATE: ConsentState = {
  essential: true,
  statistics: false,
  marketing: false,
};

/**
 * The cookie Polaris (privacy center GV2KOBPX4) writes on withjoy.com. Its value is
 * URL-encoded JSON:
 *
 * `{"clientId":"…","implicit":true,"analyticsPermitted":true,"personalizationPermitted":true,
 *   "adsPermitted":true,"notOptedOut":true,"essentialPermitted":true}`
 *
 * Polaris writes it for every visitor, banner or not:
 * - Region with no banner (e.g. US): `implicit: true`, every permission true.
 * - EEA/UK, banner on screen, no choice yet: `implicit: true`, every permission false.
 * - After Decline: `implicit: false`, every permission false.
 * - After Accept: `implicit: false`, every permission true.
 */
const POLARIS_COOKIE_PREFIX = 'polaris_consent_settings=';

/** How often to re-read a placeholder cookie while waiting for Polaris to rewrite it. */
const PLACEHOLDER_POLL_MS = 25;

/** How long to wait for Polaris to rewrite a placeholder cookie before deciding from it. */
const PLACEHOLDER_WAIT_MS = 250;

type PolarisSettings = Readonly<Record<string, unknown>>;

/**
 * Parse the `polaris_consent_settings` cookie.
 *
 * @returns The settings object, `'absent'` when there is no cookie, or `'malformed'` when the
 * cookie cannot be decoded or is not a JSON object
 */
function readPolarisSettings(): PolarisSettings | 'absent' | 'malformed' {
  const entry = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(POLARIS_COOKIE_PREFIX));

  if (!entry) return 'absent';

  let settings: unknown;
  try {
    settings = JSON.parse(decodeURIComponent(entry.substring(POLARIS_COOKIE_PREFIX.length)));
  } catch (e) {
    consentLogger.warn('Malformed Polaris consent cookie:', e);
    return 'malformed';
  }

  if (settings === null || typeof settings !== 'object') {
    consentLogger.warn('Polaris consent cookie is not an object:', settings);
    return 'malformed';
  }

  return settings as PolarisSettings;
}

/**
 * Whether the cookie looks like the placeholder Polaris writes on a visitor's first page before
 * it knows their region: `implicit: true` with no permission granted. Polaris rewrites it moments
 * later with the real values. An EEA visitor who has not chosen yet has the same cookie, which is
 * why the wait for a rewrite is bounded.
 */
function isPlaceholder(settings: ReturnType<typeof readPolarisSettings>): boolean {
  return (
    typeof settings === 'object' &&
    settings.implicit === true &&
    settings.analyticsPermitted !== true &&
    settings.adsPermitted !== true &&
    settings.personalizationPermitted !== true
  );
}

/**
 * Read consent from the `polaris_consent_settings` cookie.
 *
 * Statistics is `analyticsPermitted` and marketing is `adsPermitted`, each granted only when it
 * is the boolean `true`. The cookie is the only input: Polaris's GPP `cmpDisplayStatus` reports
 * `hidden` even while its banner is on screen, so it cannot say whether consent is required.
 * A missing or malformed cookie denies non-essential consent.
 *
 * @returns The current consent state for all categories
 */
export function getConsentState(): ConsentState {
  return withWindow(() => {
    const settings = readPolarisSettings();

    if (typeof settings !== 'object') {
      consentLogger.log(`Polaris consent cookie ${settings} — denying non-essential consent`);
      return CONSENT_DENIED_STATE;
    }

    return {
      essential: true,
      statistics: settings.analyticsPermitted === true,
      marketing: settings.adsPermitted === true,
    };
  }, CONSENT_DENIED_STATE);
}

/**
 * Wait for Polaris to be ready, then call callback with the consent state read from its cookie.
 *
 * Polaris is ready when its GPP `ping` answers `cmpStatus: 'loaded'` and `signalStatus: 'ready'`.
 * If it is not ready yet, the GPP `signalStatus` event with `data: 'ready'` settles it.
 *
 * The cookie is read one task after Polaris says it is ready, and re-read while it still looks
 * like Polaris's first-visit placeholder. On a visitor's first page, Polaris writes a placeholder
 * cookie (`implicit: true`, every permission false), raises `signalStatus: 'ready'`, and only
 * then rewrites the cookie for the visitor's region. Measured on withjoy.com on 2026-10-17: the
 * placeholder was still there at the event and in a microtask after it, and the final values
 * landed about 1 ms later. So a placeholder is re-read every 25 ms for up to 250 ms, and the
 * decision is made as soon as it no longer looks like one. A placeholder that stays is a genuinely pending EEA
 * visitor and is denied. Any other cookie, or none, is decided at the first read.
 *
 * The callback runs exactly once per page load. A choice the visitor makes later on the banner
 * applies from the next page load (see docs/issues/telemetry-bugs.md). Polaris never sends the
 * GPP `useractioncomplete` event, so it cannot be used to detect that choice.
 *
 * @param callback - Function to call when consent is ready, receives the consent state
 * @returns Cleanup function to remove event listeners (call when component unmounts)
 *
 * @example
 * ```typescript
 * const cleanup = onConsentReady((consent) => {
 *   console.log('Consent state:', consent);
 *   initializeAnalytics(consent);
 * });
 *
 * // Later, when cleaning up:
 * cleanup();
 * ```
 */
export function onConsentReady(callback: (consent: ConsentState) => void): () => void {
  return withWindow(
    (global) => {
      const gpp = global.__gpp;
      if (!gpp) {
        consentLogger.warn('GPP API not available — denying non-essential consent');
        callback(CONSENT_DENIED_STATE);
        return () => {};
      }

      let stopped = false;
      // Set by whichever ready signal comes first, so the callback runs at most once.
      let readTimer: number | undefined;
      // GPP assigns the listener an id in the first event it sends; removal needs it.
      let listenerId: number | undefined;

      // Cleanup clears the pending timer, so this never runs after cleanup.
      const readWhenFinal = (source: string, waitedMs: number) => {
        if (waitedMs < PLACEHOLDER_WAIT_MS && isPlaceholder(readPolarisSettings())) {
          readTimer = global.setTimeout(() => readWhenFinal(source, waitedMs + PLACEHOLDER_POLL_MS), PLACEHOLDER_POLL_MS);
          return;
        }
        const consent = getConsentState();
        consentLogger.log(`Polaris ready (${source}), consent state:`, consent);
        callback(consent);
      };

      const onReady = (source: string) => {
        if (stopped || readTimer !== undefined) return;
        readTimer = global.setTimeout(() => readWhenFinal(source, 0), 0);
      };

      gpp('ping', (pingData) => {
        consentLogger.log('Checking consent state:', pingData);
        if (pingData?.cmpStatus === 'loaded' && pingData.signalStatus === 'ready') {
          onReady('ping');
        }
      });

      gpp('addEventListener', (event) => {
        if (listenerId === undefined && typeof event?.listenerId === 'number') {
          listenerId = event.listenerId;
          // Cleanup ran before GPP assigned the id, so it could not remove the listener then.
          if (stopped) {
            gpp('removeEventListener', () => {}, listenerId);
            return;
          }
        }
        if (event?.eventName === 'signalStatus' && event.data === 'ready') {
          onReady('signalStatus event');
        }
      });

      return () => {
        stopped = true;
        global.clearTimeout(readTimer);
        if (listenerId !== undefined) {
          gpp('removeEventListener', () => {}, listenerId);
        }
      };
    },
    () => {}
  );
}
